/**
 * derived.ts — recompute all effective rates from raw state each tick.
 *
 * Nothing here is stored on GameState; it is always recomputed (on every tick
 * and on load), which prevents save-tampering and migration drift. The UI reads
 * the same snapshot for its readouts.
 */
import type { GameState } from '../core/GameState';
import { safeNumber, clamp } from '../core/numbers';
import {
  BALANCE,
  BUILDING_BY_ID,
  BASE_STORAGE,
  WORKER_BY_ID,
  TOOL_BY_LEVEL,
  ORE_BY_DEPTH,
} from '../data';
import { computeMultipliers, type Multipliers } from './modifiers';
import { comboMultiplier } from './combo';

export interface Derived {
  mults: Multipliers;
  clickPower: number;
  /** Raw ore mined per second on the focused layer (post-throttle). */
  miningRate: number;
  /** Raw ore consumed / ingots produced per second by smelters (post-throttle). */
  smeltRate: number;
  /** Research points produced per second (post-throttle). */
  researchRate: number;
  energyProduced: number;
  energyConsumed: number;
  /** 1 = fully powered; < 1 = energy deficit throttling machines. */
  throttle: number;
  /** Per-stack storage capacity. */
  storage: number;
  /** Efficiency factor of the focused layer (hardness + tool penalty). */
  layerEfficiency: number;
  focusOreId: string;
}

function buildingCount(state: GameState, id: string): number {
  return state.buildings[id] ?? 0;
}

export function computeDerived(state: GameState): Derived {
  const mults = computeMultipliers(state);

  const tool = TOOL_BY_LEVEL[state.mining.toolLevel] ?? TOOL_BY_LEVEL[1]!;
  const clickPower = tool.clickPower * mults.click * mults.global * comboMultiplier(state);

  // --- Energy balance ---
  const pp = BUILDING_BY_ID['powerplant']!;
  const energyProduced = buildingCount(state, 'powerplant') * pp.power * mults.energy;

  let energyConsumed = 0;
  for (const id of Object.keys(state.buildings)) {
    const b = BUILDING_BY_ID[id];
    if (b && b.energy < 0) energyConsumed += (state.buildings[id] ?? 0) * -b.energy;
  }
  for (const id of Object.keys(state.workers)) {
    const w = WORKER_BY_ID[id];
    if (w && w.energy < 0) energyConsumed += (state.workers[id] ?? 0) * -w.energy;
  }

  const throttle =
    energyConsumed <= 0 ? 1 : clamp(energyProduced / energyConsumed, BALANCE.minThrottle, 1);

  // --- Focused-layer efficiency ---
  const focusOre = ORE_BY_DEPTH[state.mining.focusDepth] ?? ORE_BY_DEPTH[0]!;
  const toolOk = state.mining.toolLevel >= focusOre.requiredToolLevel;
  const layerEfficiency = (1 / focusOre.hardness) * (toolOk ? 1 : BALANCE.underToolPenalty);

  // --- Mining rate (drills + workers) ---
  const drill = BUILDING_BY_ID['drill']!;
  const drillContribution = buildingCount(state, 'drill') * drill.power * mults.auto;
  let workerContribution = 0;
  for (const id of Object.keys(state.workers)) {
    const w = WORKER_BY_ID[id];
    if (w) workerContribution += (state.workers[id] ?? 0) * w.rate;
  }
  workerContribution *= mults.worker * mults.auto;
  const miningRate =
    (drillContribution + workerContribution) * mults.global * layerEfficiency * throttle;

  // --- Smelting ---
  const smelter = BUILDING_BY_ID['smelter']!;
  const smeltRate =
    buildingCount(state, 'smelter') * smelter.power * mults.smelt * mults.global * throttle;

  // --- Research ---
  const lab = BUILDING_BY_ID['lab']!;
  const researchRate = buildingCount(state, 'lab') * lab.power * mults.global * throttle;

  // --- Storage ---
  const warehouse = BUILDING_BY_ID['warehouse']!;
  const storage = BASE_STORAGE + buildingCount(state, 'warehouse') * warehouse.power;

  return {
    mults,
    clickPower: safeNumber(clickPower),
    miningRate: safeNumber(miningRate),
    smeltRate: safeNumber(smeltRate),
    researchRate: safeNumber(researchRate),
    energyProduced: safeNumber(energyProduced),
    energyConsumed: safeNumber(energyConsumed),
    throttle: safeNumber(throttle, 1),
    storage: safeNumber(storage, BASE_STORAGE),
    layerEfficiency: safeNumber(layerEfficiency),
    focusOreId: focusOre.id,
  };
}
